import React, { useState, useEffect, useCallback } from 'react';

// GPS Coordinate Input Component
// Accepts and outputs coordinates as strings in format: "latitude, longitude"
// Example: "40.7128, -74.0060"
const GPSCoordinateInput = ({
                                value,
                                onChange,
                                placeholder = 'Enter coordinates (lat, lng)',
                                className = '',
                                readOnly = false
                            }) => {
    const [input, setInput] = useState(value || '');
    const [error, setError] = useState('');
    const [isValid, setIsValid] = useState(false);


    const validateCoordinates = useCallback((coordString) => {
        if (!coordString.trim()) {
            setError('');
            setIsValid(false);
            return null;
        }


        // Decimal degrees validation pattern
        const decimalPattern = /^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/;
        const match = coordString.match(decimalPattern);
        if (!match) {
            setError('Invalid format. Use: latitude, longitude (e.g., 40.7128, -74.0060)');
            setIsValid(false);
            return null;
        }

        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);

        // Validate ranges
        if (lat < -90 || lat > 90) {
            setError('Latitude must be between -90 and 90 degrees');
            setIsValid(false);
            return null;
        }

        if (lng < -180 || lng > 180) {
            setError('Longitude must be between -180 and 180 degrees');
            setIsValid(false);
            return null;
        }

        setError('');
        setIsValid(true);
        return { lat, lng };
    }, []);

    const handleInputChange = (e) => {
        if (readOnly) return; // Don't process changes if readonly

        const value = e.target.value;
        setInput(value);

        const coords = validateCoordinates(value);
        if (coords && onChange) {
            // Return string format instead of object
            onChange(`${coords.lat}, ${coords.lng}`);
        } else if (onChange) {
            // Return empty string if invalid
            onChange('');
        }
    };

    const formatForGoogleMaps = (coordString) => {
        const coords = validateCoordinates(coordString);
        if (!coords) return '';
        return `${coords.lat},${coords.lng}`;
    };

    const openInGoogleMaps = () => {
        const formattedCoords = formatForGoogleMaps(input);
        if (formattedCoords) {
            const url = `https://www.google.com/maps?q=${formattedCoords}`;
            window.open(url, '_blank');
        }
    };

    useEffect(() => {
        setInput(value || '');
        // Validate the initial/updated value to show map icon if valid
        validateCoordinates(value || '');
    }, [value, validateCoordinates]);

    return (
        <div className={`gps-input-container ${className}`}>
            <div className="flex gap-2">
                <div className="flex-1">
                    <input
                        type="text"
                        value={input}
                        onChange={handleInputChange}
                        placeholder={placeholder}
                        readOnly={readOnly}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                            readOnly
                                ? 'bg-gray-100 cursor-not-allowed border-gray-200'
                                : error
                                    ? 'border-red-500 focus:ring-red-500'
                                    : isValid
                                        ? 'border-green-500 focus:ring-green-500'
                                        : 'border-gray-300 focus:ring-blue-500'
                        }`}
                    />
                    {/* Only show validation messages when not readonly */}
                    {!readOnly && error && (
                        <p className="text-red-500 text-sm mt-1">{error}</p>
                    )}
                    {!readOnly && isValid && !error && (
                        <p className="text-green-500 text-sm mt-1">✓ Valid coordinates</p>
                    )}
                </div>

                {/* Always show map button when valid, even in readonly mode */}
                {isValid && (
                    <button
                        type="button"
                        onClick={openInGoogleMaps}
                        className="h-10 w-10 bg-green-500 text-white rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center justify-center"
                        title="Open in Google Maps"
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                            <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                    </button>
                )}
            </div>

            <div className="text-xs text-gray-500 mt-1">
                Decimal degrees (latitude, longitude) - for Google Maps
            </div>
        </div>
    );
};

export default GPSCoordinateInput;